import { DASHBOARD_ROUTE, LOGIN_ROUTE, SIGNUP_ROUTE } from "@app/app-routing.constants";
import { WebsocketRequest, WebsocketService } from "@app/shared/databases/websocket";
import { Observable, from, throwError } from "rxjs";
import { catchError, take, tap, withLatestFrom } from "rxjs/operators";

import { Injectable } from "@angular/core";
import { RouterActions } from "@app/root-store";
import { isEmbedded } from "@app/shared/resources/embedded-mobile-checker";
import { TSLoggingService } from "@app/shared/services/ts-logging.service";
import { AuthenticatedWSUser } from "@app/user/model/user";
import { UserDbService } from "@app/user/service/user-db.service";
import { UserCredential } from "firebase/auth";

import { LOG_CATEGORY, LOG_SUB_TYPE, LOG_TYPE, createAuditLogKey } from "@app/audit-log/services/audit-log.constants";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { PendingChangesGuard } from "@app/project/services/pending-changes.guard";
import { AuthenticationReqs } from "../model/auth-user";
import { AuthUserService } from "./auth-user.service";
import { FirebaseAuthService } from "./firebase-auth.service";

export enum TS_AUTH_OPERATIONS {
    FB_SIGN_IN_ERROR = "firebase_sign_in_error",
    FB_SIGN_IN_ACTION_ERROR = "sign_in_action_error",
    FB_REGISTRATION_ATTEMPT = "firebase_registration_attempt",
    FB_REGISTRATION_SUCCESS = "firebase_registration_success",
    FB_REGISTRATION_ERROR = "firebase_registration_error",
    FB_REGISTRATION_ACTION_ERROR = "registration_action_error",
}

export enum TS_ENTITIES {
    SSO_DB = "ssopgrest",
}
/* eslint-disable @typescript-eslint/naming-convention */

interface ValidateInviteCodeRequestData extends WebsocketRequest {
    check_invite_code_data: Record<string, string>;
}

const userDbEndpoint = "userinfo";
const inviteCodeRequestType = "check_invite_code";

/**
 * Service used to perform tasks between auth effect and database, websocket or session
 */
@Injectable({ providedIn: "root" })
export class AuthUserDbService {
    constructor(
        private readonly websocketService: WebsocketService,
        private readonly authUserService: AuthUserService,
        private readonly firebaseAuth: FirebaseAuthService,
        private readonly tsLoggingService: TSLoggingService,
        private readonly userDbService: UserDbService,
        private readonly pendingChangesGuard: PendingChangesGuard,
        private readonly auditLogService: AuditLogService
    ) {}

    warningDialogConfig = {
        title: "Leave Page",
    };

    canDeactivate(): Observable<boolean> | boolean {
        this.warningDialogConfig.title = `Leave Page`;
        // returning true will navigate without confirmation
        // returning false will show a confirm dialog before navigating away
        return false;
    }

    public signupPath = `/${SIGNUP_ROUTE}`;
    public loginPath = `/${LOGIN_ROUTE}`;
    /**
     * Key used to store loginPending state in browser's LocalStorage
     */
    private readonly loginPendingKey = "loginPending";

    /**
     * Gets pendingLogin value in LocalStorage
     *
     * @description Gets the loginPending from LocalStorage.
     * loginPending is used when the user uses social sign in.
     * We need a way to track when the user has left our site while in the process of logging in.
     * Before the user leaves our site, we set the loginPending to true.
     * When the user returns from the third-party website, we set loginPending to false
     * @returns {boolean} Is the browser in a pending login state or not
     */
    get isPendingLoginState(): boolean {
        return window.localStorage?.getItem(this.loginPendingKey) === "true" || false;
    }

    /**
     * Sets the pendingLogin value in LocalStorage
     *
     * @param {boolean} pendingState value to use for loginPending
     * @description See getPendingLoginState for explanation of how loginPending is used
     */
    set isPendingLoginState(pendingState: boolean) {
        window.localStorage.setItem(this.loginPendingKey, pendingState.toString());
    }

    /**
     * Clear the SSO and Firebase session
     *
     * @description Dispatch an error if necessary, close the socket and dispatch the sign out action if not embedded.
     * If in embedded mode, the page is redirected to the mobile reload page.
     * @param {Error} [error] The error object to process during sign out
     * @param {string} [redirectTo=""] The url to redirect to after signing out
     */
    triggerSignOut({ error, redirectTo = "" }: { error?: Error; redirectTo?: string } = {}): void {
        console.log("Triggering sign out. redirectTo: ", redirectTo);
        if (error) {
            console.error("Triggering sign out. Error: ", error);
        }

        if (!isEmbedded()) {
            // Toast error
            const errorName = error?.name;
            if (errorName) {
                let timeOutMessage = error.message;
                if (errorName === "TimeoutError") {
                    timeOutMessage = "There was a problem communicating with the server.";
                }
                this.authUserService.dispatchSessionExpired(timeOutMessage);
            }

            if (this.websocketService.isWizardOpen) {
                this.websocketService.refreshConnection(this.firebaseAuth.getFirebaseUser());
                this.warningDialogConfig.title = `Leave ${this.websocketService.wizardTitle}?`;
                this.pendingChangesGuard.leavePage(this).subscribe(() => {
                    this.logSignOut();
                    window.localStorage.setItem("processSignOut", "true");
                    this.websocketService.closeSession();
                    this.authUserService.signOut(redirectTo);
                });
            } else {
                // Complete the returned observable to cancel any existing subscriptions
                this.logSignOut();
                this.websocketService.closeSession();
                this.authUserService.signOut(redirectTo);
            }
        } else {
            // redirect to the reload page
            this.authUserService.dispatchMobileReload(redirectTo);
        }
    }

    logSignOut(): void {
        this.authUserService
            .getAuthUser()
            .pipe(take(1))
            .subscribe(authUser => {
                const logKey = createAuditLogKey(LOG_CATEGORY.SYSTEM, LOG_TYPE.USER, LOG_SUB_TYPE.LOG_OUT);
                this.auditLogService.logEvent(logKey, {
                    entityId: authUser.entityId,
                    data: `Logged Out`,
                });
            });
    }

    /**
     * Subscribes to logout observer to complete the sign out process
     *
     * @description Subscribes to the logout observer in the websocket and calls triggerSignOut along with the current URL.
     * This will complete the sign out process when the close observer is called for any reason other than a manual user sign out.
     */
    subscribeToWebsocketLogoutObserver() {
        this.websocketService.logoutObserver
            .pipe(withLatestFrom(this.authUserService.selectCurrentUrl$(), this.authUserService.selectCurrentUrlQueryParams$()))
            .subscribe(([e, url, params]: [Error, string, any]) => {
                let redirect = url.split("?", 2)[0];

                // Avoid setting redirectTo to login page
                if (this.isLoginUrl(redirect)) {
                    redirect = params?.redirectTo || "";
                }
                this.triggerSignOut({ error: e, redirectTo: redirect });
            });
    }

    /**
     * Set the socket token and check the connection and set the authUser
     *
     * @param {string} fbToken Firebase token received from the Firebase SDK after sign in
     * @param {string} inviteCode Invite code used to look up new user registrations in the DB
     * @description Sets the token in the websocket and handles responses to the connect attempts.
     * Upon successful connection, it will set the authUser, the websocket token and unsubscribe from itself.
     * If it does not successfully connect, it will log a message to the time series database
     * @returns An observable with the response from the websocket
     */
    setSocketToken(fbToken: string, inviteCode?: string): Observable<AuthenticatedWSUser> {
        return this.websocketService.setToken(fbToken, inviteCode).pipe(
            tap((response: AuthenticatedWSUser) => {
                if (!this.isValidUser(response)) {
                    throw Error(response);
                }
            }),
            catchError((err: Error) => {
                this.logEvent("sign in", "websocket_connection_error", {
                    err,
                    fbToken,
                    inviteCode,
                });
                throw err;
            })
        );
    }

    /**
     * Log a user-related TS audit event
     *
     * @param {string} entityType The entity type
     * @param {string} operation Name of operation to be tracked in event database
     * @param {Record<string, any>} eventData JSON data to be sent to event database
     * @description Proxy method to Time Series Logging service
     */
    logEvent(entityType: string, operation: string, eventData: Record<string, any>) {
        this.tsLoggingService.logNoSessionEventData(null, "user", entityType, operation, null, eventData);
    }

    /**
     * @description Type guard to determine if user is valid
     * @param {(AuthenticatedWSUser | string)} user The user or error string
     * @returns {*}  {user is AuthenticatedWSUser}
     */
    private isValidUser(user: AuthenticatedWSUser | string): user is AuthenticatedWSUser {
        return (user as AuthenticatedWSUser)?.rawId !== undefined;
    }

    /**
     * Get user info from postgreSQL database from Firebase UID
     *
     * @description Get the userinfo, role and permissions from the DB for the given user
     * @param user Firebase user info that is returned from websocket on successful connection event
     * @returns the user with SSO info
     */
    getUserInfo(user: AuthenticatedWSUser) {
        // Check for expected fields
        if (user.email === undefined) {
            console.error("User missing email", user);
        }
        if (user.rawId === undefined) {
            console.error("User missing git id", user);
        }
        return this.userDbService.getLoggedInUserInfo(user.rawId).pipe(take(1));
    }

    /**
     * Is the URL a sign up or login page
     *
     * @description Determines if the url is the sign up or login url
     * @param {string} url URL to Check
     * @returns {boolean} is the URL a login or sign up URL?
     */
    isLoginOrSignUpUrl(url: string): boolean {
        return url.startsWith(this.signupPath) || url.startsWith(this.loginPath);
    }

    /**
     * @description Determins whether the URL is a login URL
     * @param {string} url the URL
     * @returns {boolean} is the URL a login URL?
     */
    isLoginUrl(url: string): boolean {
        return url.startsWith(this.loginPath);
    }

    /**
     * @description Determins whether the URL is a sign up URL
     * @param {string} url the URL
     * @returns {boolean} is the URL a sign up URL?
     */
    isSignUpUrl(url: string): boolean {
        return url.startsWith(this.signupPath) || (url.startsWith("/login/social") && url.indexOf("inviteCode=") > 0);
    }

    /**
     * Get the URL to redirect the user to after login
     *
     * @description Get the appropriate redirect page based on redirect URL
     * @param {string} redirect Page to redirect to, if supplied
     * @param {boolean} dropQueryParams should the query params be ignored and dropped?
     * @returns {RouterActions.RouteInfo} the prepared route info
     */
    getLoginRedirectUrl(redirect: string, dropQueryParams: boolean): RouterActions.RouteInfo {
        // By default, redirect to dashboard with no extras
        const route = { path: [`/${DASHBOARD_ROUTE}`], query: null, extras: null };

        if (!dropQueryParams) {
            if (redirect === undefined) {
                // If no redirect given
                // merge the query params onto the dashboard URL
                route.extras = { queryParamsHandling: "merge" };
            } else if (redirect !== "/") {
                // If the redirect isn't "/" (dashboard), split the URL and extract the query params
                const urlParts = redirect.split("?");
                let params = {};

                if (urlParts[1]) {
                    params = urlParts[1].split("&").reduce((obj, param) => {
                        const paramParts = param.split("=");
                        obj[paramParts[0]] = paramParts[1];
                        return obj;
                    }, {});
                }

                // Set the path and params
                route.path = [urlParts[0]];
                route.query = params;
            }
        }

        return route;
    }

    /**
     * Log a Firebase registration TS audit success event
     *
     * @description Log a registration success event to the Time Series DB
     * @param {UserCredential} data User data from registration process
     * @param {string} inviteCode The invitce code used to look up a new user in the user database
     */
    logFirebaseRegistrationSuccess(data: UserCredential, inviteCode?: string): void {
        const logData = data?.user;
        this.logEvent(TS_ENTITIES.SSO_DB, TS_AUTH_OPERATIONS.FB_REGISTRATION_SUCCESS, {
            /* eslint-disable @typescript-eslint/naming-convention */
            firebase_user: logData
                ? {
                      displayName: logData.displayName,
                      email: logData.email,
                      uid: logData.uid,
                  }
                : "no firebase user",
            invite_code: inviteCode,
            /* eslint-enable @typescript-eslint/naming-convention */
        });
    }

    /**
     * Log an attempted Firebase registration TS audit event
     *
     * @description Log a registration attempt event to the Time Series DB
     * @param {AuthenticationReqs} user User data from registration process
     */
    logRegistrationAttempt(user: AuthenticationReqs): void {
        this.logEvent(TS_ENTITIES.SSO_DB, TS_AUTH_OPERATIONS.FB_REGISTRATION_ATTEMPT, {
            /* eslint-disable @typescript-eslint/naming-convention */
            display_name: user.displayName,
            invite_code: user.inviteCode,
            /* eslint-enable @typescript-eslint/naming-convention */
        });
    }

    /**
     * Log a Firebase registration TS audit error event
     *
     * @description Log a registration error event to the Time Series DB
     * @param {AuthenticationReqs} data User data from registration process
     * @param {Error} error Error thrown by registration process
     * @returns {Observable<Error>} an error to throw during a registration step
     */
    throwAndlogFirebaseRegistrationError(data: AuthenticationReqs, error: Error): Observable<Error> {
        this.logEvent(TS_ENTITIES.SSO_DB, TS_AUTH_OPERATIONS.FB_REGISTRATION_ERROR, {
            /* eslint-disable @typescript-eslint/naming-convention */
            display_name: data.displayName,
            invite_code: data.inviteCode,
            error,
            /* eslint-enable @typescript-eslint/naming-convention */
        });

        return throwError(() => error);
    }

    /**
     * @description Type guard to determine if user is valid
     * @param {(UserCredential | Error)} user The user or error
     * @returns {*}  {user is UserCredential}
     */
    isUserCredential(user: UserCredential | Error): user is UserCredential {
        return (user as UserCredential).user !== undefined;
    }

    /**
     * Register the user with Firebase email/password account
     *
     * @description Register the user data with Firebase and return an observable result
     * @param {AuthenticationReqs} auth User data from registration process
     * @returns {Observable<UserCredential | Error>} the created user or the error generated
     */
    registerFirebaseUser$(auth: AuthenticationReqs): Observable<UserCredential | Error> {
        this.logRegistrationAttempt(auth);
        // Create an observable from the registration promise
        return from(this.firebaseAuth.createUserWithEmailAndPassword(auth.email, auth.password)).pipe(
            tap(user => this.logFirebaseRegistrationSuccess(user)),
            catchError((error: Error) => this.throwAndlogFirebaseRegistrationError(auth, error))
        );
    }

    parseFirebaseErrorMessage(error: Error | string) {
        let message: string;

        if (typeof error === "string") {
            try {
                const parsedError = JSON.parse(error) as { error: string };
                message = parsedError?.error;
            } catch (_) {
                message = error;
            }
        } else {
            message = error?.message || "There was an error during sign up";
        }

        if (message.includes("invite_code not found")) {
            message = "This invite link has already been used. Please request a new one.";
        } else if (message.includes("email mismatch")) {
            message =
                "You attempted to sign up with an email address that did not match the invite." +
                " Please retry the sign up link with the email address in the invite.";
        } else if (message.includes("user not found")) {
            message =
                "There was an error signing in. If you used a sign up link to get here, the link may be invalid." +
                " If not, please check that you properly completed the sign up process.";
        }

        return message;
    }

    public validateInviteCode(data: { inviteCode: string }): Observable<any> {
        return this.websocketService.makeRequest(
            {
                request_type: inviteCodeRequestType,
                session_id: "",
                check_invite_code_data: {
                    invite_code: data.inviteCode,
                },
                metadata: {},
            } as ValidateInviteCodeRequestData,
            false
        );
    }
}
